import { getLocalizedValue } from "@features/drivers/data";
import { isOriginalPriceEnabled } from "@features/products/data";
import {
  DiscountType,
  ProductMediaEntityType,
  type ProductDetailsEntity,
  type ProductDetailsVariantEntity,
} from "@features/products/domain";
import * as variantsResolver from "@taager-experience-shared/variants";
import type {
  ProductDetailsModel,
  ProductDetailsVariantModel,
} from "../models";

export function mapProductDetails(
  product: ProductDetailsModel,
): ProductDetailsEntity {
  return {
    id: product.productId,
    categoryId: product.categoryId,
    options: {
      colors: [
        ...new Set(
          product.variants
            .flatMap(variant => variant.attributes)
            .filter(attr => attr?.key === "color")
            .map(attr => attr!.value),
        ),
      ].map(colorValue => {
        return {
          name: getLocalizedValue(
            variantsResolver.getColorByHexCode(colorValue),
          ),
          hex: colorValue,
        };
      }),
      sizes: [
        ...new Set(
          product.variants
            .flatMap(variant => variant.attributes)
            .filter(attr => attr?.key === "size")
            .map(attr => attr!.value),
        ),
      ],
    },
    variants: product.variants.map(variant =>
      mapProductDetailsVariant(product, variant),
    ),
  };
}

function mapProductDetailsVariant(
  product: ProductDetailsModel,
  variant: ProductDetailsVariantModel,
): ProductDetailsVariantEntity {
  const colorAttribute = variant.attributes?.find(attr => attr.key === "color");
  const sizeAttribute = variant.attributes?.find(attr => attr?.key === "size");

  const discounts = variant.financials.discounts.map(discount => ({
    ...discount,
    originalPrice: (discount.price || 0) - (discount.profit || 0),
  }));

  const saleDiscount = discounts?.find(
    discount => discount.type === DiscountType.SALE,
  );

  let finalPrice: number;

  if (isOriginalPriceEnabled()) {
    finalPrice =
      saleDiscount?.originalPrice ?? variant.financials.originalPrice;
  } else {
    finalPrice = saleDiscount?.price ?? variant.financials.price;
  }

  return {
    // TODO: Remove this after product details migration is complete
    newParentId: product.productId,
    legacyParentId: product.productLegacyId,
    legacyVariantId: variant.variantLegacyId,

    isFavorite: variant.merchantInfo.isFavourite,

    name: product.name,
    sku: variant.sku,
    stats: {
      confirmationRate: variant.analytics?.confirmationRate
        ? Math.round(variant.analytics.confirmationRate * 100)
        : undefined,
      deliveryRate: variant.analytics?.deliveryRate
        ? Math.round(variant.analytics.deliveryRate * 100)
        : undefined,
    },
    bundle: variant.bundleItems?.map(bundleItem => ({
      productId: bundleItem.productId,
      sku: bundleItem.sku,
      image: bundleItem.thumbnail,
    })),
    availability: variant.availability,
    quickDetails: variant.description,
    keywords: variant.keywords,
    howToUse: variant.howToUse,
    contentIdeas: variant.specifications,
    lockingRequestAvailable: variant.merchantInfo.lockingRequestAvailable,
    media: [
      ...(variant.images || []).map(image => {
        const extension = image.split(".").pop()!;
        return {
          type:
            extension === "mp4"
              ? ProductMediaEntityType.VIDEO
              : ProductMediaEntityType.IMAGE,
          url: image,
          extension,
        };
      }),
      ...(variant.videos || []).map(video => ({
        type: ProductMediaEntityType.VIDEO,
        url: video,
        extension: video.split(".").pop()!,
      })),
    ],
    parentId: product.productId,
    variantId: variant.variantLegacyId,
    type: product.type,
    thumbnail: variant.thumbnail,
    lockInfo: {
      isLocked: variant.merchantInfo.lockInfo.isLocked,
      isOpenForTesting: variant.merchantInfo.orderTestingAvailable,
      isLockingAvailable: variant.merchantInfo.lockingRequestAvailable,
      orderTestingStarted: variant.merchantInfo.orderTestingStarted,
      quantity: variant.merchantInfo.lockInfo.quantity,
      quantityUsed: variant.merchantInfo.lockInfo.quantityUsed,
      ...(variant.merchantInfo.lockInfo.expiresAt && {
        expiresAt: new Date(variant.merchantInfo.lockInfo.expiresAt),
      }),
      isExpired:
        variant.merchantInfo.lockInfo.isExpired ||
        (variant.merchantInfo.lockInfo.expiresAt
          ? new Date(variant.merchantInfo.lockInfo.expiresAt) < new Date()
          : undefined),
    },
    orderTestingInfo: variant.orderTestingInfo,
    financials: {
      originalPrice: variant.financials.originalPrice,
      price: variant.financials.price,
      profit: variant.financials.profit,
      discounts,

      tieredPricing: Array.isArray(variant.financials?.tieredPricing)
        ? variant.financials.tieredPricing.map(tier => ({
            quantity: tier.quantity,
            price: tier.price,
            profit: tier.profit,
            dynamicPrice: tier.dynamicPrice,
            dynamicProfit: tier.dynamicProfit,
            maxPrice: tier.maxPrice,
          }))
        : undefined,
      finalPrice,
      finalProfit:
        variant.financials.discounts?.find(
          discount => discount.type === DiscountType.SALE,
        )?.profit ?? variant.financials.profit,
      pricingRange: {
        minPrice:
          variant.financials.pricingRange?.min ||
          saleDiscount?.price ||
          variant.financials.price,
        maxPrice: variant.financials.pricingRange?.max,
      },
    },
    isMultiVariant: product.variants.length > 1,
    isPrimary: variant.isPrimary,
    stockAvailability: {
      stockRange: variant.stockAvailability?.stockRange,
      detailedStockRange: variant.stockAvailability?.detailedStockRange,
    },
    isOnSale: Boolean(
      variant.financials.discounts?.some(
        discount => discount.type === DiscountType.SALE,
      ),
    ),
    hasSecondPlacementDiscount: Boolean(
      variant.financials.discounts?.some(
        discount => discount.type === DiscountType.SECOND_PLACEMENT,
      ),
    ),
    hasAdditionalQuantityDiscount: Boolean(
      variant.financials.discounts?.some(
        discount => discount.type === DiscountType.ADDITIONAL_QUANTITY,
      ),
    ),
    color: colorAttribute?.value
      ? {
          name: getLocalizedValue(
            variantsResolver.getColorByHexCode(colorAttribute.value),
          ),
          hex: colorAttribute.value,
        }
      : undefined,
    size: sizeAttribute?.value,
    orderable: variant.merchantInfo.orderable,
    preorderable: variant.merchantInfo.preorderable,
  };
}
